// src/pages/Utilisateurs/PrepaPhysique/PrepaPhysique.jsx
import React, { useEffect, useState } from "react";
import { useUser } from "../../Home/UserContext";
import { getRoleFromRoleId } from "../../../utils/roleHelper";
import useScrollToTop from "../../../hooks/useScollToTop";
import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";
import InfoBubble from "../../../components/InfoBubble/InfoBubble";
import PrepaPhysiqueList from "./PrepaPhysiqueList";
import { getAdherentsByUser } from "../../../services/adherentServices";
import { getExercisesByAdherent } from "../../../services/prepaPhysiqueServices";
import { ToastContainer, toast } from "react-toastify";

const PrepaPhysique = () => {
  useScrollToTop();
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  const [loading, setLoading] = useState(true);
  const [adherents, setAdherents] = useState([]);
  const [exercisesByAdherent, setExercisesByAdherent] = useState({});

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const list = await getAdherentsByUser();
        const safeList = Array.isArray(list) ? list : [];
        setAdherents(safeList);

        // Charge les exercices en parallèle pour chaque adhérent
        const entries = await Promise.all(
          safeList.map(async (ad) => {
            try {
              const res = await getExercisesByAdherent(ad.id);
              return [ad.id, res?.Exercices || []];
            } catch {
              return [ad.id, []];
            }
          })
        );
        setExercisesByAdherent(Object.fromEntries(entries));
      } catch (e) {
        console.error("Erreur lors de la récupération des exercices :", e);
        toast.error("Erreur lors de la récupération des exercices.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <section className="w-full">
      {/* Offset navbar */}
      <div className="pt-[150px]" aria-hidden />

      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
        {/* Titre page */}
        <HeaderTitle
          role="Utilisateur"
          title="Préparation Physique"
          align="center"
          className="mb-1"
        />

        {/* Navigation secondaire */}
        <div className="mt-3 mb-6">
          <DashboardNavigation role={userRole} />
        </div>

        {/* Carte principale */}
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur mb-4">
          {/* Bandeau dégradé */}
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500">
            <div className="px-5 py-6 sm:px-8 text-white">
              <h2 className="text-lg sm:text-xl font-semibold text-center">
                Vos séances – Force, cardio, mobilité & prévention
              </h2>
              <p className="text-sm/6 opacity-95 max-w-3xl mx-auto text-center mt-1">
                Consultez vos exercices assignés par vos enseignants et
                cochez-les une fois réalisés.
              </p>
              {/* Aide / Infos */}
              <div className="px-5 py-3 sm:px-8">
                <p>
                  ✅ Cette page affiche vos exercices de préparation physique.
                </p>
                <p>
                  ✅ Les enseignants gèrent vos programmes et vos répétitions.
                </p>
                <p>✅ Vous pouvez marquer vos exercices comme réalisés.</p>
              </div>
            </div>
          </div>

          {/* Contenu */}
          <div className="px-5 pb-6 sm:px-8">
            {loading ? (
              // Loaders
              <div className="space-y-4">
                {[...Array(2)].map((_, i) => (
                  <div
                    key={i}
                    className="animate-pulse h-36 rounded-2xl ring-1 ring-slate-200 dark:ring-slate-800 bg-white/70 dark:bg-slate-900/50"
                  />
                ))}
              </div>
            ) : adherents.length === 0 ? (
              <div className="text-center py-12 text-slate-600 dark:text-slate-400">
                Aucun adhérent rattaché à votre compte.
              </div>
            ) : (
              <div className="space-y-6">
                {adherents.map((ad) => {
                  const list = exercisesByAdherent[ad.id] || [];
                  return (
                    <div
                      key={ad.id}
                      className="rounded-2xl overflow-hidden ring-1 ring-slate-200 dark:ring-slate-800 bg-white/90 dark:bg-slate-900/60"
                    >
                      {/* Header de carte (nom + compteur) */}
                      <div className="px-5 py-3 bg-slate-50/70 dark:bg-slate-900/70 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
                        <div className="min-w-0">
                          <h3 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100 truncate">
                            {ad.prenom} {ad.nom}
                          </h3>
                        </div>
                        <span className="ml-3 text-xs px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 ring-1 ring-slate-200/70 dark:ring-slate-700/70">
                          {list.length} exercice{list.length > 1 ? "s" : ""}
                        </span>
                      </div>

                      {/* Liste des exercices de l’adhérent */}
                      <div className="p-4 sm:p-6">
                        <PrepaPhysiqueList
                          exercices={list}
                          adherentId={ad.id}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <ToastContainer />
    </section>
  );
};

export default PrepaPhysique;
